// Панель фильтров над списком. Разметка зафиксирована в каталоге паттернов
// (docs/v6/00-PATTERN-CATALOG.md §3.2) и до этого была скопирована на пяти
// страницах — вместе с инлайновыми стилями поиска и каждого <select>.
//
// Warm Precision (spec D): hairline-бордер, тёплая поверхность контейнера,
// радиус M10, единая высота контролов 28px.
import type { ChangeEvent, ReactElement, ReactNode } from 'react';
import { Input } from 'antd';

interface FilterBarProps {
  /** Поле поиска — всегда слева. */
  search?: ReactNode;
  /** Фильтры и действия — прижимаются вправо. */
  children?: ReactNode;
}

export function FilterBar({ search, children }: FilterBarProps): ReactElement {
  return (
    <div className="crm-radius-m flex w-full shrink-0 flex-col items-stretch gap-3 border border-[var(--ant-color-border-secondary)] bg-[var(--ant-color-bg-container)] px-3.5 py-2 sm:flex-row sm:items-center">
      {search ? (
        <div className="w-full min-w-0 sm:max-w-[400px] sm:flex-1" data-testid="filter-bar-search">
          {search}
        </div>
      ) : null}
      <div className="flex min-w-0 flex-wrap items-center gap-2 sm:ml-auto">{children}</div>
    </div>
  );
}

interface FilterSearchProps {
  id?: string;
  'aria-label': string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onPressEnter?: (value: string) => void;
}

export function FilterSearch({
  id,
  'aria-label': ariaLabel,
  placeholder,
  value,
  onChange,
  onPressEnter,
}: FilterSearchProps): ReactElement {
  return (
    <Input
      {...(id ? { id } : {})}
      aria-label={ariaLabel}
      {...(placeholder ? { placeholder } : {})}
      value={value}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
      {...(onPressEnter
        ? { onPressEnter: (event) => onPressEnter(event.currentTarget.value) }
        : {})}
      variant="borderless"
      className="crm-radius-m transition-colors"
      style={{ backgroundColor: 'var(--g-fill)', height: 28, fontSize: 12, padding: '0 10px' }}
      allowClear
    />
  );
}

export interface FilterOption {
  value: string;
  label: string;
}

interface FilterSelectProps {
  'aria-label': string;
  value: string;
  onChange: (value: string) => void;
  options: FilterOption[];
}

/**
 * Нативный `<select>` — так сделаны все фильтры в проекте: он компактнее
 * antd Select и не открывает портал поверх таблицы.
 */
export function FilterSelect({
  'aria-label': ariaLabel,
  value,
  onChange,
  options,
}: FilterSelectProps): ReactElement {
  return (
    <select
      aria-label={ariaLabel}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="bg-transparent font-medium transition-colors dark:border-stone-600"
      style={{
        height: 28,
        border: '1px solid var(--ant-color-border)',
        borderRadius: 'var(--ant-border-radius, 10px)',
        padding: '0 10px',
        backgroundColor: 'var(--ant-color-bg-container)',
        color: 'var(--ant-color-text)',
        fontSize: 12,
        outline: 'none',
        cursor: 'pointer',
      }}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
